import React, { useEffect, useRef } from "react";

import { containerId, needsContainer } from "../../utils/adConfig";
import { isYandexRefreshable, renderYandex, useAdRefresh } from "../../utils/adRefresh";

/**
 * Один вызов `Ya.Context.AdvManager.render` для любого яндексового плейсмента.
 *
 * Заменяет четыре компонента, которые различались только тем, откуда брать
 * контейнер: YandexAd (собирал `yandex_rtb_<blockId>`), AdvManager (брал
 * `option.renderTo`), YandexFullScreen (брал `option.blockId`) и YandexFloorAd
 * (контейнера не имел, зато умел `delay`).
 */
export function YandexAdapter({ entry }) {
    const blockId = entry ? entry.blockId : null;
    const delay = entry ? entry.delay : null;
    const ref = useRef(null);
    // Как у adfox: блок обновляется и при уходе из экрана, иначе рефреш ждал бы
    // полный VIEW_DURATION непрерывного показа и при скролле почти не случался.
    const round = useAdRefresh(ref, {
        enabled: Boolean(blockId) && needsContainer(entry) && isYandexRefreshable(entry.type),
        refreshOnLeave: true,
    });

    useEffect(() => {
        if (!blockId || typeof window === "undefined" || !window.yaContextCb) {
            return undefined;
        }

        const options = { blockId };

        if (entry.type) {
            options.type = entry.type;
        }
        if (entry.platform) {
            options.platform = entry.platform;
        }
        if (needsContainer(entry)) {
            options.renderTo = containerId(entry);
        }

        const render = () => renderYandex(options, ref.current, round);

        // Задержка — только у первого показа: рефреш и так ждёт, пока блок провисит в экране.
        if (!delay || round) {
            render();

            return undefined;
        }

        const timer = setTimeout(render, delay);

        return () => clearTimeout(timer);
    }, [blockId, delay, round]);

    if (!entry || !blockId || !needsContainer(entry)) {
        return null;
    }

    return <div ref={ref} id={containerId(entry)} />;
}

export default YandexAdapter;
